import { Typography } from '@mui/material';
import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { DeliveryKind } from '@work4all/models/lib/Classes/DeliveryKind.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../../tooltip/Tooltip';
import { ListEntityPicker } from '../components/list-entity-picker/ListEntityPicker';
import { IPickerProps } from '../types';

export type IDeliveryKindPickerProps<TMultiple extends boolean> = IPickerProps<
  DeliveryKind,
  TMultiple
>;

export function DeliveryKindPicker<TMultiple extends boolean>(
  props: IDeliveryKindPickerProps<TMultiple>
) {
  const { data, ...rest } = props;

  const allFields = useMemo(() => {
    return { ...PAYMENT_KIND_FIELDS, ...data };
  }, [data]);

  const request = useMemo<DataRequest>(() => {
    return {
      data: allFields,
      entity: Entities.deliveryKind,
      completeDataResponse: true,
      operationName: 'GetDeliveryKinds',
    };
  }, [allFields]);

  const response = useDataProvider(request);

  // The data provider does not set the `total` property when
  // `completeDataResponse` is `true`.   So we have to set it manually.
  // Otherwise, the <ListEntityPicker /> will not be able to render the list.
  const patchedResponse = useMemo(() => {
    return { ...response, total: response.data.length };
  }, [response]);

  const renderItem = (deliveryKind: DeliveryKind) => {
    const label = deliveryKind.text;

    return (
      <Tooltip title={label}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <ListEntityPicker
      entity={Entities.deliveryKind}
      data={allFields}
      filterBy={'text'}
      sortBy="text"
      {...rest}
      fixedDataSet={patchedResponse}
      renderItemContent={renderItem}
    />
  );
}

const PAYMENT_KIND_FIELDS: DeliveryKind = {
  id: null,
  text: null,
  longtext: null,
};
